/**
 * theme/preferences.ts —— **设置面事实源**（§7 三条机制 S1–S3 的实装位）。
 *
 * 依据（逐字）：
 *  ·《UI 全局布局壳规范》v1.2 §7 —— 十一组项 ＋ **S1** 两个入口读**同一份事实**（面板 ↔ `/` 命令）
 *    ＋ **S2** 写入**带 revision 围栏**＋**退役全量替换路径** ＋ **S3** 作用面显式标注。
 *  ·《UI 整改施工方案件》§3 —— **禁复列已退役设置项**（推理强度/权限档/排队-插话二选/会话模型/会话权限/cwd）。
 *  · ★A-034（20261006）：① 面板不再自陈机制；② S2 **真退役**——公开写面**只剩 `update`／`setSessionView`**
 *    （全量替换路径已下线，类内私有提交不上公开面）；③ S3 **写时约束**——写入口按声明 `scope` 分区：
 *    `read_only` 拒写（结构化 `PreferencesViolation`）、`current_session` 只许写 `session_view`（唯一入口
 *    `setSessionView`）、`global`／`future_sessions` 写全局段（唯一入口 `update`）；越 scope 即红。
 *
 * 纪律：
 *  · 本件**不取数**（零网络：一切出网只经 `transport/`）；只承载**UI 偏好**的读写与版本围栏。
 *  · 持久化**只落 UI 偏好**（键闭集 `PREF_KEYS`）——**不含任何凭据/token**（`web/CONTRACT.md` §6 不变）。
 *  · **禁**逐项旧路径：任何写入都经 `update()`／`setSessionView()`（同一提交核心，带 revision 围栏）；
 *    已退役项无写入口。
 */
import { SHELL_DECL, type SettingScope } from "../shell/shellDecl";
import { clampFontSize, FONT_SIZE_DEFAULT } from "./tokens";
import { E_CODES } from "../transport/errorText";

/** 主题（§7：浅色／跟随系统；**不提供暗色**，无 `dark` 值） */
export type ThemeChoice = "light" | "system";
/** 动效（§7：跟随系统／始终关闭） */
export type MotionChoice = "system" | "off";
/** 显示密度（§7：标准／紧凑，**默认紧凑**） */
export type DensityChoice = "standard" | "compact";
/** Cmd/Ctrl+Enter 模式（§7） */
export type SubmitMode = "enter" | "cmd_ctrl_enter";
/** 繁忙投递（§7：**唯一真实繁忙通道＝插话**；不提供排队/插话二选） */
export type BusyDelivery = "steer";

export const THEME_CHOICES: readonly ThemeChoice[] = ["light", "system"];
export const MOTION_CHOICES: readonly MotionChoice[] = ["system", "off"];
export const DENSITY_CHOICES: readonly DensityChoice[] = ["standard", "compact"];
export const SUBMIT_MODES: readonly SubmitMode[] = ["enter", "cmd_ctrl_enter"];
export const BUSY_DELIVERIES: readonly BusyDelivery[] = ["steer"];

/** 持久化键闭集（只 UI 偏好；**无凭据类键**） */
export const PREF_KEYS = [
  "theme", "font_size", "motion", "density", "default_model",
  "submit_mode", "busy_delivery", "session_view",
] as const;

export const PREFERENCES_STORAGE_KEY = "xg.prefs.v1";

export interface Preferences {
  theme: ThemeChoice;
  font_size: number;
  motion: MotionChoice;
  density: DensityChoice;
  /** §7 默认模型（供应商分组选中项 id；空＝未选，**不补造**） */
  default_model: string;
  submit_mode: SubmitMode;
  busy_delivery: BusyDelivery;
  /** §7 会话级视图偏好（sessionId → 视图名；持久化） */
  session_view: Record<string, string>;
}

export interface PreferencesSnapshot {
  /** revision 围栏（S2）：写入须带当前 revision，落后即拒 */
  revision: number;
  values: Preferences;
}

export class PreferencesViolation extends Error {
  constructor(message: string) {
    super(E_CODES.PREFERENCES + "：" + message);
    this.name = "PreferencesViolation";
  }
}

export const PREFERENCES_DEFAULT: Preferences = {
  theme: "system",
  font_size: FONT_SIZE_DEFAULT,
  motion: "system",
  density: "compact",
  default_model: "",
  submit_mode: "enter",
  busy_delivery: "steer",
  session_view: {},
};

/** 闭集校验＋夹取（越集即抛；字号夹到 12–17） */
export function validatePreferences(raw: Partial<Preferences>): Preferences {
  const merged: Preferences = {
    ...PREFERENCES_DEFAULT,
    ...raw,
    session_view: { ...(raw.session_view ?? {}) },
  };
  merged.font_size = clampFontSize(merged.font_size);
  if (!THEME_CHOICES.includes(merged.theme)) throw new PreferencesViolation("主题越集：" + merged.theme);
  if (!MOTION_CHOICES.includes(merged.motion)) throw new PreferencesViolation("动效越集：" + merged.motion);
  if (!DENSITY_CHOICES.includes(merged.density)) throw new PreferencesViolation("密度越集：" + merged.density);
  if (!SUBMIT_MODES.includes(merged.submit_mode)) throw new PreferencesViolation("提交模式越集：" + merged.submit_mode);
  if (!BUSY_DELIVERIES.includes(merged.busy_delivery)) {
    throw new PreferencesViolation("繁忙投递越集（唯一真实通道＝插话）：" + merged.busy_delivery);
  }
  if (typeof merged.default_model !== "string") throw new PreferencesViolation("默认模型非字符串");
  return merged;
}

/**
 * §7·S3 **写面作用面约束**（★A-034③）——键 → 声明 `scope` 的**唯一来源**＝
 * `shell.decl.yaml::settings.groups[].scope`（**禁**在写面自造第二份 scope 表）。
 */
export function declaredScopeOf(key: string): SettingScope | undefined {
  return SHELL_DECL.settings.groups.find((group) => group.id === key)?.scope;
}

/** 全局段可写作用面：`global`／`future_sessions`（写 `Preferences` 全局段；`update` 唯一入口）。 */
const GLOBAL_SEGMENT_SCOPES: readonly SettingScope[] = ["global", "future_sessions"];

/**
 * S3 写时约束（`update`＝**全局段**写入口）：逐键按声明 scope 判——
 *  · 无声明（未知键）⇒ 拒（不静默落库）；
 *  · `read_only` ⇒ 拒（结构化错误；只读项无写入口）；
 *  · `current_session` ⇒ 拒（会话级只许写 `session_view`，唯一入口 `setSessionView`）；
 *  · `global`／`future_sessions` ⇒ 许（写全局段）。
 * 越 scope 即红（不静默、不部分落库：整批先判后提交）。
 */
export function assertGlobalSegmentWritable(patch: Partial<Preferences>): void {
  for (const key of Object.keys(patch)) {
    const scope = declaredScopeOf(key);
    if (!scope) throw new PreferencesViolation("未知设置项（无声明 scope）：" + key);
    if (scope === "read_only") {
      throw new PreferencesViolation("只读项拒写（scope=read_only）：" + key);
    }
    if (scope === "current_session") {
      throw new PreferencesViolation("会话级项**只许**写 session_view（走 setSessionView；scope=current_session）：" + key);
    }
    if (!GLOBAL_SEGMENT_SCOPES.includes(scope)) {
      throw new PreferencesViolation("作用面越界（越 scope 即红；scope=" + scope + "）：" + key);
    }
  }
}

/** 存储面（浏览器 localStorage 的**仅偏好**最小面；缺失/不可用 ⇒ 内存态，不报错） */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** 默认存储：浏览器 localStorage；不可用（隐私模式/测试）⇒ undefined ⇒ 纯内存 */
export function defaultStorage(): StorageLike | undefined {
  try {
    const store = (globalThis as { localStorage?: StorageLike }).localStorage;
    if (!store) return undefined;
    return store;
  } catch {
    return undefined;
  }
}

/**
 * 设置事实源（S1：面板与命令共用一个实例/一份快照）。
 * S2（★A-034② **真退役**）：**公开写面只剩 `update`／`setSessionView`**——原公开的
 *   `replaceAll`（全量替换路径）已下线，提交核心 `#commit` 为**类内私有**（不上公开面）。
 * S3（★A-034③）：两个写入口按声明 `scope` 分区（见 `assertGlobalSegmentWritable`）。
 */
export class PreferencesStore {
  private state: PreferencesSnapshot;
  private readonly listeners = new Set<(snap: PreferencesSnapshot) => void>();

  constructor(private readonly storage: StorageLike | undefined = defaultStorage()) {
    this.state = this.read();
  }

  /** 当前快照（只读；面板与命令同读此值） */
  snapshot(): PreferencesSnapshot {
    return { revision: this.state.revision, values: { ...this.state.values, session_view: { ...this.state.values.session_view } } };
  }

  /**
   * **全局段**写入口（S2：**带 revision 围栏**；`expectedRevision` 不匹配 ⇒ 拒，不静默覆盖）。
   * S3：逐键按声明 `scope` 约束——`read_only` 拒、`current_session` 拒（走 `setSessionView`）、
   *     `global`／`future_sessions` 许；**禁第二写路径**。
   */
  update(patch: Partial<Preferences>, expectedRevision?: number): PreferencesSnapshot {
    this.#assertRevision(expectedRevision);
    assertGlobalSegmentWritable(patch);
    return this.#commit({ ...this.state.values, ...patch });
  }

  /** §7 会话级视图偏好（持久化；无偏好 ⇒ 空串，由壳取 home） */
  viewFor(sessionId: string | undefined): string {
    if (!sessionId) return "";
    return this.state.values.session_view[sessionId] ?? "";
  }

  /**
   * **会话级**（`current_session`）写入口（S3：只许写 `session_view`；写路径同 `#commit` ⇒ 带围栏）。
   * 不得借此写全局段（本方法只改 `session_view` 一枝）。
   */
  setSessionView(sessionId: string, viewName: string): PreferencesSnapshot {
    if (!sessionId) throw new PreferencesViolation("会话级视图偏好缺会话 id（不落空键）");
    return this.#commit({
      ...this.state.values,
      session_view: { ...this.state.values.session_view, [sessionId]: viewName },
    });
  }

  subscribe(listener: (snap: PreferencesSnapshot) => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  /** revision 围栏（S2）：写入须带当前 revision，落后即拒。 */
  #assertRevision(expectedRevision?: number): void {
    if (expectedRevision !== undefined && expectedRevision !== this.state.revision) {
      throw new PreferencesViolation(
        "revision 围栏不符：期望 " + expectedRevision + "，现值 " + this.state.revision,
      );
    }
  }

  /** 提交核心（**类内私有**，不上公开写面）：校验 ⇒ revision 递增 ⇒ 落库 ⇒ 通知。 */
  #commit(next: Preferences): PreferencesSnapshot {
    const values = validatePreferences(next);
    this.state = { revision: this.state.revision + 1, values };
    this.persist();
    for (const listener of this.listeners) listener(this.snapshot());
    return this.snapshot();
  }

  private read(): PreferencesSnapshot {
    const empty: PreferencesSnapshot = { revision: 0, values: { ...PREFERENCES_DEFAULT } };
    if (!this.storage) return empty;
    let raw: string | null = null;
    try {
      raw = this.storage.getItem(PREFERENCES_STORAGE_KEY);
    } catch {
      return empty;
    }
    if (!raw) return empty;
    try {
      const parsed = JSON.parse(raw) as Partial<PreferencesSnapshot>;
      const revision = typeof parsed.revision === "number" && parsed.revision >= 0 ? parsed.revision : 0;
      return { revision, values: validatePreferences(parsed.values ?? {}) };
    } catch {
      return empty;                       // 坏档 ⇒ 回默认（不吞错为“成功”，只是不采用坏值）
    }
  }

  private persist(): void {
    if (!this.storage) return;
    const payload: Record<string, unknown> = { revision: this.state.revision };
    const values: Record<string, unknown> = {};
    for (const key of PREF_KEYS) values[key] = this.state.values[key];
    payload.values = values;
    try {
      this.storage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // 落库失败不影响本次会话内生效（偏好是呈现面，不是账）
    }
  }
}

/** 命令行入口（S1：与面板**读同一份事实**） */
export interface CommandResult { handled: boolean; note: string }

/**
 * `/theme`／`/font`／`/model`／`/density`／`/motion` 命令 ⇒ 同一 `PreferencesStore`。
 * 与面板同读写路径（同围栏、同校验）；未知命令 ⇒ `handled:false`（交上行通道，不吞）。
 */
export function applySettingsCommand(text: string, store: PreferencesStore): CommandResult {
  const trimmed = text.trim();
  if (!trimmed.startsWith("/")) return { handled: false, note: "" };
  const [head, ...rest] = trimmed.slice(1).split(/\s+/);
  const arg = rest.join(" ");
  const revision = store.snapshot().revision;
  switch (head) {
    case "theme":
      store.update({ theme: arg as ThemeChoice }, revision);
      return { handled: true, note: "主题＝" + arg };
    case "font":
      store.update({ font_size: Number(arg) }, revision);
      return { handled: true, note: "正文字号＝" + clampFontSize(Number(arg)) };
    case "density":
      store.update({ density: arg as DensityChoice }, revision);
      return { handled: true, note: "密度＝" + arg };
    case "motion":
      store.update({ motion: arg as MotionChoice }, revision);
      return { handled: true, note: "动效＝" + arg };
    case "model":
      store.update({ default_model: arg }, revision);
      return { handled: true, note: "默认模型＝" + arg };
    default:
      return { handled: false, note: "" };
  }
}
